import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import { useDispatch } from "react-redux";
import { openAddRequestInfoModal } from "store/slices/openItem";
import { OpenItemStatus } from "types/Models/openItem";
import { pluralize } from "utils/pluralize";
import { ChatCardButton } from "./ChatCardButton";
import ArrowsDownUp from "static/images/ArrowsDownUp.svg";
import TinySuccess from "static/images/TinySuccess.svg";
import { ActionItemArchivedMessage } from "components/MessageBubble/ActionItemArchivedMessage";
import { Sync } from "components/icons/Sync";
import { Button } from "components/DesignSystem/Button/Button";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import {
  DefaultStreamChatGenerics,
  useChannelActionContext,
  useChannelStateContext,
} from "stream-chat-react";
import { Info } from "components/icons/Info";
import { Tag } from "components/DesignSystem/Tag/Tag";

export const RequestedInfoChatCard = ({
  customData,
  groupId,
  messageId,
  channelId,
  size,
  isDeprecated,
}: {
  customData: {
    card_status: OpenItemStatus;
    csv_id: string;
    header: string;
    status: OpenItemStatus;
    transaction_count: number;
    submitted_transaction_count?: number;
    transaction_ids: string;
    entity_id: string;
  };
  groupId: string;
  messageId: string;
  channelId: string;
  size?: "regular" | "block";
  isDeprecated: boolean;
}) => {
  const dispatch = useDispatch();
  const isRequested = customData.status === "REQUESTED";
  const { jumpToMessage } =
    useChannelActionContext<DefaultStreamChatGenerics>("MessageActionsBox");
  const { channel } = useChannelStateContext<DefaultStreamChatGenerics>();

  const onAction = () => {
    dispatch(
      openAddRequestInfoModal({
        csvId: customData?.csv_id,
        groupId,
        messageId,
        channelId,
        entityId: customData?.entity_id || "",
        transactionIds: customData?.transaction_ids || "",
        status: customData?.status || "",
      })
    );
  };

  const jumpToActiveMsg = () => {
    const latestMessageCardId = (channel as any)?.data?.latest_message_card_id;
    if (latestMessageCardId) {
      dispatch(setScrollToMessageId(latestMessageCardId));
      jumpToMessage(latestMessageCardId!);
    }
  };

  const urlText = isRequested ? "Add Info" : "View Info";
  const isArchived = customData.status === "ARCHIVED";

  return (
    <ChatCard.Root size={size}>
      <ChatCard.Header>
        <div className="t-gap-1.5 t-flex t-items-center">
          <Info stroke="1.2" />
          <div>{customData.header}</div>
        </div>
      </ChatCard.Header>
      <ChatCard.Body>
        <div className="t-flex t-gap-3 t-items-center">
          <div className="t-h-6 t-w-6 t-rounded-full t-border t-border-solid t-border-neutral-0 t-flex t-items-center t-justify-center t-relative">
            <div className="t-text-neutral -t-mt-0.5">
              <img src={ArrowsDownUp} alt="ArrowsDownUp" />
            </div>
            {isRequested ? (
              <div className="t-min-w-1.5 t-min-h-1.5 t-max-h-1.5 t-max-w-1.5 t-rounded-full t-bottom-0 t-right-0 t-absolute t-bg-orange" />
            ) : (
              <img
                src={TinySuccess}
                alt="TinySuccess"
                className="t-bottom-0 t-right-0 t-absolute"
              />
            )}
          </div>
          <div className="t-text-subtext-sm t-text-text-60">
            {pluralize(
              customData?.transaction_count,
              "transaction",
              "transactions"
            )}
          </div>
        </div>
        {isArchived && (
          <div className="t-flex t-items-center t-gap-1.5 t-text-blue-70 t-text-body-sm t-px-2 t-py-1 t-rounded t-bg-blue-10">
            <Sync />
            <div>The request has been updated.</div>
          </div>
        )}
        {isRequested && (
          <div>
            <Tag tagType="orange" rounded icon={false}>
              {customData.submitted_transaction_count} of{" "}
              {customData.transaction_count} completed
            </Tag>
          </div>
        )}
        {isDeprecated ? (
          <ActionItemArchivedMessage />
        ) : isArchived ? (
          <Button size="small" onClick={jumpToActiveMsg}>
            View updated request
          </Button>
        ) : (
          <ChatCardButton
            isRequested={isRequested}
            urlText={urlText}
            handleCardAction={onAction}
          />
        )}
      </ChatCard.Body>
    </ChatCard.Root>
  );
};
